import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { I18n, getDateLocaleCode } from '../../../../helpers/i18n';
import { getDeviceTimezone } from '../../../../helpers/timezone';

export { getDeviceTimezone };

/** HH:mm in device local timezone */
export const formatSessionClock = (epoch) => moment(epoch).format('HH:mm');

/** HH:mm - HH:mm range in device local timezone */
export const formatSessionClockRange = (startEpoch, endEpoch) =>
  formatSessionClock(startEpoch) + ' - ' + formatSessionClock(endEpoch);

/** DD/MM/YYYY in device local timezone */
export const formatSessionDate = (epoch) => moment(epoch).format('DD/MM/YYYY');

/** DD-MM-YYYY in device local timezone (rating / legacy display) */
export const formatSessionDateDashed = (epoch) => moment(epoch).format('DD-MM-YYYY');

/** Weekday label + DD/MM/YYYY in device local timezone */
export const formatSessionDay = (epoch, days) => {
  const local = moment(epoch);
  return days[local.day()] + ', ' + formatSessionDate(epoch);
};

/**
 * Prefer local format from epoch when present; otherwise keep API preformatted string.
 * Membership history often only returns strings (no epoch) — those stay as-is (API TZ).
 */
export const formatMembershipTime = (epochOrString, pattern = 'HH:mm DD/MM/YYYY') => {
  if (epochOrString === null || epochOrString === undefined || epochOrString === '' || epochOrString === 'N/A') {
    return epochOrString === 'N/A' ? 'N/A' : '';
  }
  if (typeof epochOrString === 'number' || (typeof epochOrString === 'string' && /^\d+$/.test(epochOrString))) {
    return moment(Number(epochOrString)).format(pattern);
  }
  return String(epochOrString);
};

/** HH:mm + weekday + DD/MM/YYYY in device local timezone */
export const formatSessionDateTimeWithDay = (epoch, days) =>
  formatSessionClock(epoch) + ' ' + days[moment(epoch).day()] + ' ' + formatSessionDate(epoch);

/** HH:mm + DD/MM/YYYY in device local timezone (dateEpoch optional, defaults to epoch) */
export const formatSessionClockWithDate = (epoch, dateEpoch = epoch) =>
  formatSessionClock(epoch) + ' ' + formatSessionDate(dateEpoch);

export const convertToUTC = (localTime) => {
  const date = moment(localTime, 'HH:mm').toDate()
  return date.toISOString().split('T')[1].substring(0, 5);
};

export const convertToGMTLocal = (localTime) => {
  const momentDate = moment.utc(localTime, "HH:mm");
  return momentDate.local().format("HH:mm");
};

const keyDaysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/** Canonical English weekday key — independent of moment.locale (vi/en UI). */
export const weekdayKeyFromMoment = (m) => keyDaysOfWeek[moment(m).day()];

export const daysOfWeek = () => {
  const locale = getDateLocaleCode();
  const weekdaysObject = {};
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });

  keyDaysOfWeek.forEach((day, index) => {
    const date = new Date(2024, 9, 27 + index);
    weekdaysObject[day] = formatter.format(date);
  });

  return weekdaysObject;
};

export const getDayOfWeek = (dateStr) => {
  if (!dateStr) {
    return dateStr
  }
  const date = new Date(dateStr);
  return keyDaysOfWeek[date.getDay()];
}

/** English weekday key (sunday..saturday) — uses the moment's utc/local mode, not moment.locale(). */
export const getEnglishWeekday = (date) => {
  const m = moment.isMoment(date) ? date : moment(date);
  return keyDaysOfWeek[m.day()];
};
export const slotDefault = { start: '08:00', end: '08:45' };

// Return `desired` if it's an allowed option in `durationTimes`, otherwise
// fall back to the first available option. Keeps a valid duration selected
// when the allowed list changes (e.g. it now depends on the user's level).
export const pickValidDuration = (durationTimes, desired) => {
  if (!Array.isArray(durationTimes) || durationTimes.length === 0) {
    return desired;
  }
  const isValid = durationTimes.some((option) => Number(option.value) === Number(desired));
  return isValid ? Number(desired) : Number(durationTimes[0].value);
};

export const createSlotDefault = (duration = 45) => {
  const startTime = moment('08:00', 'HH:mm');
  const endTime = startTime.clone().add(duration, 'minutes');
  return { start: '08:00', end: endTime.format('HH:mm') };
};

export const dataTimesDefault = (duration = 45) => {
  const week = daysOfWeek();
  return Object.keys(week).map((key) => (
    { startDate: key, label: week[key], data: [createSlotDefault(duration)], apply: !['sunday', 'saturday'].includes(key) }
  ))
}

function getTargetDate(day, timeString, isUTC = false) {
  const [hours, minutes] = timeString.split(':').map(Number);

  const today = moment().endOf('day');

  const targetDayIndex = keyDaysOfWeek.indexOf(day);
  const todayDayIndex = today.day();
  let daysUntilNextDay = targetDayIndex - todayDayIndex;

  if (daysUntilNextDay < 0) {
    daysUntilNextDay += 7;
  }

  const targetDate = today.clone().add(daysUntilNextDay, 'days');

  if (isUTC) {
    return targetDate.utc().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  } else {
    return targetDate.local().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
  }
}

export const changeDateLocaleToUTC = (day, timeString) => {
  const originalDate = getTargetDate(day, timeString, false);
  const utcDate = originalDate.clone().utc();
  return keyDaysOfWeek[utcDate.day()];
};

export const changeDateUTCToLocale = (day, timeString) => {
  const originalDate = getTargetDate(day, timeString, true);

  const localDate = originalDate.clone().local();

  const originalDateFormat = originalDate.format('YYYY-MM-DD');
  const localDateFormat = localDate.format('YYYY-MM-DD');

  if (originalDateFormat !== localDateFormat) {
    return keyDaysOfWeek[localDate.day()];
  }

  return null;
};

export const convertUtcToLocal = (date, time) => {
  const datetime = `${date} ${time}`;
  const utcMoment = moment.utc(datetime, "YYYY-MM-DD HH:mm");
  const localMoment = utcMoment.local();
  return localMoment.format("YYYY-MM-DD");
}

export const convertLocalToUtc = (date, time) => {
  const datetime = `${date} ${time}`;
  const utcMoment = moment(datetime, "YYYY-MM-DD HH:mm");
  const localMoment = utcMoment.utc();
  return localMoment.format("YYYY-MM-DD");
}

/** Calendar-day payload for BE calendarBoundUtc — local YMD, no UTC shift. */
export const getTimeRangeUtc = (timeSlotStart, timeSlotEnd) => {
  if (!timeSlotStart || !timeSlotEnd) {
    return {
      timeSlotStart: timeSlotStart,
      timeSlotEnd: timeSlotEnd,
    }
  }
  timeSlotStart = moment(timeSlotStart);
  timeSlotEnd = moment(timeSlotEnd);
  const format = 'DD/MM/YYYY HH:mm:ss';
  let newSlotStart = timeSlotStart.clone().startOf('day');
  let newSlotEnd = timeSlotEnd.clone().endOf('day');
  return {
    timeSlotStart: newSlotStart.format(format),
    timeSlotEnd: newSlotEnd.format(format),
  }
}

/**
 * UTC calendar YMD from API topic/limit bounds (00:00Z / 23:59Z).
 * Always read as UTC so end-of-day Z does not shift +1 in UTC+ zones.
 */
export const toCalendarBoundYmd = (isoOrDate) => {
  if (isoOrDate === null || isoOrDate === undefined || isoOrDate === '') {
    return null;
  }
  const m = moment.utc(isoOrDate);
  if (!m.isValid()) {
    return null;
  }
  return m.format('YYYY-MM-DD');
};

/**
 * Local moment for an API UTC calendar bound (RangePicker / isBetween).
 * @param {boolean} endOfDay - when true, local endOf('day') for inclusive range checks
 */
export const toCalendarBoundLocalMoment = (isoOrDate, endOfDay = false) => {
  const ymd = toCalendarBoundYmd(isoOrDate);
  if (!ymd) {
    return null;
  }
  const local = moment(ymd, 'YYYY-MM-DD');
  return endOfDay ? local.endOf('day') : local.startOf('day');
};

/** Hydrate RangePicker from API UTC calendar bounds (00:00Z / 23:59Z). */
export const getTimeRangeLocale = (timeSlotStart, timeSlotEnd) => {
  if (!timeSlotStart || !timeSlotEnd) {
    return {
      timeSlotStart: timeSlotStart,
      timeSlotEnd: timeSlotEnd,
    }
  }

  return {
    timeSlotStart: toCalendarBoundLocalMoment(timeSlotStart),
    timeSlotEnd: toCalendarBoundLocalMoment(timeSlotEnd),
  }
}

/**
 * Format API UTC calendar bound as a calendar date string.
 * Uses UTC YMD so end-of-day Z does not shift +1 in UTC+ timezones.
 */
export const formatCalendarBoundDate = (isoOrDate, pattern = 'DD/MM/YYYY') => {
  const ymd = toCalendarBoundYmd(isoOrDate);
  if (!ymd) {
    return '';
  }
  return moment.utc(ymd, 'YYYY-MM-DD').format(pattern);
}

/** Format API UTC calendar start–end as "DD/MM/YYYY - DD/MM/YYYY" (or custom pattern). */
export const formatCalendarBoundRange = (
  start,
  end,
  pattern = 'DD/MM/YYYY',
  separator = ' - '
) => {
  const startText = formatCalendarBoundDate(start, pattern);
  const endText = formatCalendarBoundDate(end, pattern);
  if (startText && endText) {
    return `${startText}${separator}${endText}`;
  }
  return startText || endText;
}

/** Inclusive check: local calendar day within API UTC topic/limit bounds. */
export const isLocalDateInCalendarBounds = (localDate, startBound, endBound) => {
  if (!localDate || !startBound || !endBound) {
    return true;
  }
  const input = moment(localDate).startOf('day');
  const start = toCalendarBoundLocalMoment(startBound);
  const end = toCalendarBoundLocalMoment(endBound, true);
  if (!start || !end) {
    return true;
  }
  return input.isBetween(start, end, null, '[]');
}

export const mapDateTimeToUtc = (dataTimes) => {
  let dataTime = cloneDeep(dataTimes)
  if (!dataTime || !dataTime.length) {
    return dataTime
  }
  const dateNeedAdd = {}

  dataTime.forEach(date => {
    if (!date.apply) {
      return
    }
    const slotNeedSubtract = []

    date.data.forEach((slots) => {
      const dateInWeek = changeDateLocaleToUTC(date.startDate, slots.start);
      if (!dateInWeek) {
        return
      }

      slotNeedSubtract.push(slots.start)
      if (dateNeedAdd[dateInWeek]) {
        dateNeedAdd[dateInWeek].push(slots)
      } else {
        dateNeedAdd[dateInWeek] = [slots]
      }
    })

    if (slotNeedSubtract.length) {
      date.data = date.data.filter(slots => !slotNeedSubtract.includes(slots.start))
    }

    if (!date.data.length) {
      date.apply = false
    }
  })

  if (Object.keys(dateNeedAdd).length) {
    dataTime = dataTime.map(date => {
      const newDate = {...date}
      const {startDate} = newDate
      if (dateNeedAdd[startDate]) {
        if (!newDate.apply) {
          newDate.apply = true
          newDate.data = []
        }
        newDate.data = newDate.data.concat(dateNeedAdd[startDate]);
      }
      if (newDate.apply) {
        return {
          startDate: startDate,
          data: newDate.data.map(slot => {
            const newSlot = {
              start: convertToUTC(slot.start),
              end: convertToUTC(slot.end)
            }
            if (slot.startTimeOneSlot) {
              newSlot.startTimeOneSlot = convertToUTC(slot.startTimeOneSlot);
              newSlot.endTimeOneSlot = convertToUTC(slot.endTimeOneSlot);
              newSlot.startTimeTopicOneSlot = slot.startTimeTopicOneSlot
              newSlot.endTimeTopicOneSlot = slot.endTimeTopicOneSlot
            }
            return newSlot
          }),
        }
      }
      return null
    }).filter(item => item)
  } else {
    dataTime = dataTime.filter(item => item.apply).map((day) => ({
      startDate: day.startDate,
      data: day.data.map(slot => {
        const newSlot = {
          start: convertToUTC(slot.start),
          end: convertToUTC(slot.end)
        }
        if (slot.startTimeOneSlot) {
          newSlot.startTimeOneSlot = convertToUTC(slot.startTimeOneSlot);
          newSlot.endTimeOneSlot = convertToUTC(slot.endTimeOneSlot);
          newSlot.startTimeTopicOneSlot = slot.startTimeTopicOneSlot
          newSlot.endTimeTopicOneSlot = slot.endTimeTopicOneSlot
        }
        return newSlot
      }),
    }));
  }

  return dataTime
}
